import styled from 'styled-components';
import {
  blackTangaroa,
  paradigmLightBlue,
} from '@paradigm/design-system/src/colors';
import { warningYellowIcon } from '#/assets';
import PositionTable from '#/components/UnifiedRfqs/positions/table/PositionTable';
import GroupSelection from '#/components/UnifiedRfqs/positions/GroupSelection';
import LastUpdatedAt from '#/components/UnifiedRfqs/positions/LastUpdatedAt';
import PositionFiltersModal from '#/components/UnifiedRfqs/positions/filters/PositionFiltersModal';
import PositionFiltersButton from '#/components/UnifiedRfqs/positions/filters/PositionFiltersButton';
import { default as BaseActionsBar } from '#/components/UnifiedRfqs/components/bottom-tabs/ActionsBar';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { TrackingCtxProvider } from '@paradigm/features/src/analytics';
import PositionFiltersModalProvider from '@paradigm/features/src/positions/contexts/filters/modal';
import { usePositionsManagementAccountPermissionEnabled } from './usePositionsManagementAccount';
import PositionsMarketCreation from './PositionsMarketCreation';

const POSITIONS_GETTING_STARTED =
  'https://www.paradigm.co/help/getting-started/unified-markets#positions';

export default function PositionsTab() {
  const isPositionsManagementAccountPermissionEnabled =
    usePositionsManagementAccountPermissionEnabled();

  const content = isPositionsManagementAccountPermissionEnabled ? (
    <PositionsTableContent />
  ) : (
    <AccountHasNotPositionsManagementEnabled />
  );

  return (
    <PositionFiltersModalProvider>
      <TrackingCtxProvider value="positions tab">
        <Container aria-label="positions tab">
          <PositionsActionsBar
            isDisabled={!isPositionsManagementAccountPermissionEnabled}
          />
          {content}
        </Container>
      </TrackingCtxProvider>
      <PositionFiltersModal />
    </PositionFiltersModalProvider>
  );
}

interface ActionsBarProps {
  readonly isDisabled: boolean;
}
function PositionsActionsBar({ isDisabled }: ActionsBarProps) {
  return (
    <ActionsBar isDisabled={isDisabled}>
      <PositionsMarketCreation />
      <Divider />
      <PositionFiltersButton />
      <Divider />
      <GroupSelection />
      {!isDisabled && <LastUpdatedAt />}
    </ActionsBar>
  );
}

function PositionsTableContent() {
  return <PositionTable />;
}

function AccountHasNotPositionsManagementEnabled() {
  return (
    <Message>
      <img
        className="mr-1 mb-1"
        style={{ width: 14, height: 12 }}
        src={warningYellowIcon}
        alt="warning"
      />
      Please enable &apos;Trade:read-only&apos; scope for your Venue API
      Credentials by following the instructions listed{' '}
      <Link
        href={POSITIONS_GETTING_STARTED}
        target="_blank"
        rel="noopener noreferrer"
      >
        here
      </Link>
    </Message>
  );
}

const Message = styled.p`
  margin: 18px auto;
  font-size: 12px;
  font-weight: 500;
`;

const Link = styled.a`
  color: ${paradigmLightBlue};
`;

const Container = styled.div`
  flex: 1; // take 100% heigh
  display: grid;
  grid-template-rows: 43px 1fr;
  background-color: ${blackTangaroa};
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    max-height: calc(100vh - 60px);
    overflow: auto;
    grid-template-rows: 148px 1fr;
  }
`;

const Divider = styled.div`
  height: 2px;
  width: 24px;
  transform: rotate(270deg);
  background: linear-gradient(
    270deg,
    rgba(238, 238, 238, 0) 0%,
    rgba(216, 216, 216, 0.2) 53.67%,
    rgba(216, 216, 216, 0) 100%
  );
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const ActionsBar = styled(BaseActionsBar)<{ isDisabled: boolean }>`
  display: grid;

  grid-template-columns: 146px 24px 78px 24px 300px 1fr;
  grid-template-areas: 'create . filters . grouping last';
  gap: 9px;

  ${({ isDisabled }) => isDisabled && `opacity: 0.3; pointer-events: none;`}

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    position: sticky;
    top: 0;
    z-index: 2;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr 53px;
    grid-template-areas:
      'create filters'
      'grouping grouping'
      'last last';
    max-width: 100vw;
    height: 100%;
    padding: 0;
  }
`;
